/**
 * Media cache using the Cache API.
 * Caches media files up to 5 MB (voice messages, image thumbnails, round videos)
 * for offline access and faster re-display. Files larger than 5 MB are skipped.
 */

const CACHE_NAME = "smeet-media-v1";
const MAX_CACHEABLE_SIZE = 5 * 1024 * 1024; // 5 MB — skip anything larger

function isCacheApiAvailable(): boolean {
  return typeof caches !== "undefined";
}

/**
 * Fetch a media URL with auth headers, caching the response.
 * Returns a blob URL. Caller is responsible for revoking it when done.
 *
 * @param expectedContentType — if the server returns a wrong MIME type
 *   (e.g. application/octet-stream for audio), re-wrap the blob with this type.
 */
export async function fetchWithMediaCache(
  url: string,
  authHeaders?: Record<string, string>,
  expectedContentType?: string
): Promise<string> {
  if (!isCacheApiAvailable()) {
    return fetchAndCreateBlobUrl(url, authHeaders, expectedContentType);
  }

  const cache = await caches.open(CACHE_NAME);

  // Try cache first
  const cached = await cache.match(url);
  if (cached) {
    const blob = await cached.blob();
    return URL.createObjectURL(fixBlobType(blob, expectedContentType));
  }

  // Fetch from network
  const resp = await fetch(url, {
    credentials: "include",
    headers: authHeaders || {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

  const clone = resp.clone();
  const blob = await resp.blob();

  // Only cache if response is small enough
  if (blob.size <= MAX_CACHEABLE_SIZE) {
    cache.put(url, clone).catch(() => {});
  }

  return URL.createObjectURL(fixBlobType(blob, expectedContentType));
}

/**
 * Cache an image thumbnail URL. Used by CachedImage component.
 * Returns the original URL if Cache API is not available or on error.
 * Returns a blob URL from cache if available.
 */
export async function getCachedImageUrl(url: string): Promise<string> {
  if (!isCacheApiAvailable()) return url;

  // Skip cross-origin URLs — they can't be cached due to CORS restrictions
  try {
    if (url.startsWith("http") && new URL(url).origin !== window.location.origin) {
      return url;
    }
  } catch {
    return url;
  }

  try {
    const cache = await caches.open(CACHE_NAME);
    const cached = await cache.match(url);
    if (cached) {
      const blob = await cached.blob();
      return URL.createObjectURL(blob);
    }

    // Fetch and cache in background
    const resp = await fetch(url, { credentials: "include" });
    if (!resp.ok) return url;

    const clone = resp.clone();
    const blob = await resp.blob();

    if (blob.size <= MAX_CACHEABLE_SIZE) {
      cache.put(url, clone).catch(() => {});
    }

    return URL.createObjectURL(blob);
  } catch {
    return url;
  }
}

/** Re-wrap blob if the server returned a wrong MIME type. */
function fixBlobType(blob: Blob, expectedContentType?: string): Blob {
  if (!expectedContentType) return blob;
  if (blob.type && blob.type.startsWith(expectedContentType.split("/")[0] + "/")) return blob;
  return new Blob([blob], { type: expectedContentType });
}

/** Fallback: fetch without caching. */
async function fetchAndCreateBlobUrl(
  url: string,
  authHeaders?: Record<string, string>,
  expectedContentType?: string
): Promise<string> {
  const resp = await fetch(url, {
    credentials: "include",
    headers: authHeaders || {},
  });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const blob = await resp.blob();
  return URL.createObjectURL(fixBlobType(blob, expectedContentType));
}

/** Get media cache statistics. */
export interface MediaCacheStats {
  count: number;
  estimatedSizeBytes: number;
  estimatedSizeMB: string;
}

export async function getMediaCacheStats(): Promise<MediaCacheStats> {
  if (!isCacheApiAvailable()) {
    return { count: 0, estimatedSizeBytes: 0, estimatedSizeMB: "0.00" };
  }

  try {
    const cache = await caches.open(CACHE_NAME);
    const keys = await cache.keys();
    const count = keys.length;

    // Sample up to 10 entries to estimate size
    let totalSampled = 0;
    let sampledBytes = 0;
    for (const request of keys.slice(0, 10)) {
      const resp = await cache.match(request);
      if (resp) {
        const blob = await resp.blob();
        sampledBytes += blob.size;
        totalSampled++;
      }
    }

    const avgSize = totalSampled > 0 ? sampledBytes / totalSampled : 0;
    const estimatedSizeBytes = Math.round(avgSize * count);

    return {
      count,
      estimatedSizeBytes,
      estimatedSizeMB: (estimatedSizeBytes / (1024 * 1024)).toFixed(2),
    };
  } catch {
    return { count: 0, estimatedSizeBytes: 0, estimatedSizeMB: "0.00" };
  }
}

/** Clear the entire media cache. */
export async function clearMediaCache(): Promise<void> {
  if (!isCacheApiAvailable()) return;
  try {
    await caches.delete(CACHE_NAME);
  } catch { /* ignore */ }
}
